import React, { useCallback, useRef, useState } from "react";

import { Tooltip } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  RowActionsExtra,
} from "Components/AppGrid/shipeezi-new-grid";
import { colors } from "@zapalacorp/tokens";
import { getDateValueInTimeZone } from "Components/Generic/MilestoneDates";
import { TFunction, useTranslation } from "react-i18next";
import { PiWarning } from "react-icons/pi";
import { useHistory } from "react-router-dom";
import { getHoursDiffering } from "Util/formatDateAndTime";

import ConfirmationModal from "../../../../../../../../../components/Dialogs/ConfirmationModal";
import { useAppState } from "../../../../../../../../../context/StateContext";
import { Color } from "../../../../../../../../../components/Form/Button";
import BadgeStatus from "../../../../../../../../../components/Generic/BadgeStatus";
import IconCloseCircle from "../../../../../../../../../components/Generic/Icons/CloseCircle";
import { NotificationToast } from "../../../../../../../../../components/Generic/Toast/NotificationToast";
import {
  FleetAvailability,
  FleetAvailabilityAllDayAbsence,
  FleetAvailabilityPartialAbsence,
} from "../../../../../../../../../models/fleet.availability";
import { Filters, Searches } from "../../../../../../../../../models/shipeezi.table";
import AvailabilityService from "../../../../../../../../../services/availability.service";
import {
  FleetAvailabilityStatus,
  FleetAvailabilityType,
} from "../../../../../../../../../util/enums/fleet";
import { AvailabilityKeys } from "../../../../../../../../../util/queries";
import { NewRecordButton } from "../../../../../People/Members/Member/components/NewRecordButton";
import useEquipmentActions from "../../../hooks/useEquipmentActions";
import { useEquipmentContext } from "../../context/Equipment";
import ManageAvailability, { ManageAvailabilityHandles } from "./ManageAvailability";
import {
  ClickableRowActionButton,
  ColumnDateShow,
  ColumnStatus,
  ConfirmationModalBody,
  ConfirmationModalBodyText,
  useStyles,
} from "./styles";

const getStatusBadgeColorsByAvailability = (
  row: FleetAvailability,
  translate: TFunction<"translation", undefined>
): { color: string; background: string; label: string } => {
  const { status } = row;

  switch (status?.toLowerCase()) {
    case FleetAvailabilityStatus.IN_PROGRESS.toLowerCase():
      return {
        background: colors.warning50,
        color: colors.warning500,
        label: translate("inProgress"),
      };
    case FleetAvailabilityStatus.COMPLETED.toLowerCase():
      return {
        background: colors.success50,
        color: colors.success500,
        label: translate("completed"),
      };
    case FleetAvailabilityStatus.CANCELLED.toLowerCase():
      return {
        background: colors.error50,
        color: colors.error500,
        label: translate("cancelled"),
      };
    default:
      return {
        background: colors.brandBlue50,
        color: colors.brandBlue500,
        label: translate("pending"),
      };
  }
};

type Props = { isView: boolean };

export const AvailabilityFleetTab: React.FC<Props> = ({ isView }) => {
  const {
    equipment: { id: equipmentId, fleetNumber },
  } = useEquipmentContext();

  const { t: translate } = useTranslation();
  const { userTimezone } = useAppState();
  const { location } = useHistory<{ isView?: boolean }>();
  const queryClient = useQueryClient();
  const classes = useStyles();

  const manageAvaialbilityRef = useRef<ManageAvailabilityHandles>(null);

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [openConfirmationModal, setOpenConfirmationModal] = useState<boolean>(false);
  const [availabiltyToCancel, setAvailabilityToCancel] = useState<{ id: number } | null>();
  const { getFleetAvabilities, getFleetAvabilitiesCount } = useEquipmentActions();

  const {
    refetch,
    data: availabilities,
    isFetching,
  } = getFleetAvabilities({ page, pageSize, filters, searches, equipmentId });

  const { data: availabilitiesTotal, refetch: refetchTotal } = getFleetAvabilitiesCount({
    searches,
    filters,
    equipmentId,
  });

  const { mutate: createAvailability, isLoading: isLoadingCreateAvailability } = useMutation({
    mutationKey: AvailabilityKeys.create(),
    mutationFn: async ({ data }: { data: FleetAvailability }) =>
      AvailabilityService.create({
        ...data,
        equipmentId,
      }),
    onSuccess: () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"));
      queryClient.invalidateQueries({ queryKey: [AvailabilityKeys.getAll] });
      setOpenConfirmationModal(false);
      setAvailabilityToCancel(null);
      manageAvaialbilityRef.current?.close();
      refetch();
      refetchTotal();
    },
    onError: (error) => {
      console.error(error);
      NotificationToast.error(translate("anErrorOccurred"));
    },
  });

  const { mutate, isLoading } = useMutation({
    mutationKey: AvailabilityKeys.update(),
    mutationFn: async ({ id }: { id: number }) => AvailabilityService.cancelAvailabilty(id),
    onSuccess: () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"));
      queryClient.invalidateQueries({ queryKey: [AvailabilityKeys.getAll] });
      setOpenConfirmationModal(false);
      setAvailabilityToCancel(null);
      refetch();
    },
    onError: (error) => {
      console.error(error);
      NotificationToast.error(translate("anErrorOccurred"));
    },
  });

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    const filtersKeys = Object.getOwnPropertyNames(value);

    setFilters(filtersKeys && filtersKeys?.length ? value : undefined);
  };

  const onCancelAvailabilityRow = (data: FleetAvailability) => {
    if (data?.id) {
      setAvailabilityToCancel({ id: data?.id });
      setOpenConfirmationModal(true);
    }
  };

  const onAddAvailability = (
    data: FleetAvailabilityAllDayAbsence | FleetAvailabilityPartialAbsence
  ) => {
    createAvailability({ data });
  };

  const onOpenNewAvailabilityPopup = useCallback(() => manageAvaialbilityRef.current?.open(), []);

  const renderDatetime = (date: string | undefined, showTime: boolean) => {
    const dateWithTimeZone = getDateValueInTimeZone(date, "dd/MM/yyyy", userTimezone?.name);
    const timeWithTimeZone = getDateValueInTimeZone(date, "HH:mm", userTimezone?.name);

    if (dateWithTimeZone) {
      return (
        <ColumnDateShow>
          <span>{dateWithTimeZone}</span>
          {showTime && !!timeWithTimeZone && <span>{timeWithTimeZone}</span>}
        </ColumnDateShow>
      );
    }

    return "-";
  };

  const columns: Column[] = [
    {
      name: "type",
      title: translate("type"),
    },
    {
      name: "dateFrom",
      title: translate("from"),
      hideSearch: true,
      formatComponent: (_, row?: FleetAvailability) => {
        return renderDatetime(row?.dateFrom, !row?.allDayAbsence);
      },
    },
    {
      name: "dateTo",
      title: translate("to"),
      hideSearch: true,
      formatComponent: (_, row?: FleetAvailability) => {
        const date = row?.dateTo || row?.dateFrom;

        return renderDatetime(date, !row?.allDayAbsence);
      },
    },
    {
      name: "hours",
      title: translate("hours"),
      hideSearch: true,
      formatComponent: (_, row?: FleetAvailability) => {
        if (row?.allDayAbsence) {
          return "-";
        }

        const startDate = row?.dateFrom ? new Date(row.dateFrom) : null;
        const endDate = row?.dateTo ? new Date(row.dateTo) : null;

        if (startDate && endDate) {
          startDate.setHours(startDate.getHours());
          startDate.setMinutes(startDate.getMinutes());

          endDate.setHours(endDate.getHours());
          endDate.setMinutes(endDate.getMinutes());

          return <span>{getHoursDiffering(startDate, endDate)}</span>;
        }

        return "-";
      },
    },
    {
      name: "createdBy",
      hideSearch: true,
      title: translate("createdBy"),
      formatText: (user: FleetAvailability["createdBy"]) => user?.fullName ?? "-",
    },
    {
      name: "status",
      hideSearch: true,
      title: translate("status"),
      formatComponent: (_, row?: FleetAvailability) => {
        if (!row) return "-";

        const { label, background, color } = getStatusBadgeColorsByAvailability(row, translate);

        return (
          <ColumnStatus>
            <BadgeStatus
              value={label}
              useExternalColor
              backgroundColor={background}
              textColor={color}
            />
          </ColumnStatus>
        );
      },
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleChangeStatus", "newRecord"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          column: "availability.type",
          name: translate("type"),
          options: Object.values(FleetAvailabilityType).map((option) => ({
            label: option,
            value: option,
          })),
        },
      ],
      onChange: handleFilter,
    },
    newRecord: isView ? null : (
      <NewRecordButton
        label={translate("newUnavailability")}
        onClick={onOpenNewAvailabilityPopup}
        disabled={location?.state?.isView}
      />
    ),
  };

  const rowActionsExtra: RowActionsExtra[] = [
    {
      title: "Cancel",
      buttonAndAction(rowId, row: FleetAvailability) {
        if (
          row?.status?.toLowerCase() === FleetAvailabilityStatus.CANCELLED.toLowerCase() ||
          row?.status?.toLowerCase() === FleetAvailabilityStatus.COMPLETED.toLowerCase()
        ) {
          return null;
        }

        if (!row?.id || isView) return null;

        return (
          <Tooltip title={translate("cancel")}>
            <ClickableRowActionButton disabled={location?.state?.isView}>
              <div
                onClick={() => (location?.state?.isView ? undefined : onCancelAvailabilityRow(row))}
              >
                <IconCloseCircle height={20} width={20} color={colors.error500} />
              </div>
            </ClickableRowActionButton>
          </Tooltip>
        );
      },
    },
  ];

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  return (
    <>
      <ShipeeziNewGrid
        data={availabilities}
        totalItems={Number(availabilitiesTotal)}
        columns={columns}
        showSelection={false}
        showSort={false}
        loadingData={isFetching}
        currentPage={page}
        pageSize={pageSize}
        onChangePageSize={setPageSize}
        pageSizeList={[10, 20, 40, 60]}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        positionActionsColumn="right"
        generalActions={generalActions}
        keyExtractor={(data) => data?.id?.toString()}
        rowActionsExtra={rowActionsExtra}
        emptyData={translate("noInformationToBeShown")}
      />
      <ManageAvailability
        ref={manageAvaialbilityRef}
        onAddNewAvailability={onAddAvailability}
        isLoading={isLoadingCreateAvailability}
      />

      <ConfirmationModal
        open={openConfirmationModal}
        title={translate("cancelAvailability")}
        closeModal={() => setOpenConfirmationModal(false)}
        leftIconTitle={<PiWarning color={colors.error600} />}
        loading={isLoading}
        submit={() => availabiltyToCancel && mutate(availabiltyToCancel)}
        textSubmit={translate("yesCancel")}
        textCancel={translate("noKeepIt")}
        customColorCancel={Color.TEXT_INFO}
        customColorSave={Color.ERROR}
        type="error"
        maxWidth="520px"
        modalFooterCustomClass={classes.modalFooter}
        showCancelButton
      >
        <ConfirmationModalBody>
          <ConfirmationModalBodyText>
            {translate("doYouReallyWantToCancelThisAbsence")} {`${fleetNumber}? `}
          </ConfirmationModalBodyText>
          <ConfirmationModalBodyText isError>
            {translate("thisActionCannotBeUndone")}
          </ConfirmationModalBodyText>
        </ConfirmationModalBody>
      </ConfirmationModal>
    </>
  );
};
